import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Mail, CheckCircle, AlertCircle, Loader, ArrowRight, Shield, Users, Trophy, Smartphone } from 'lucide-react'
import toast from 'react-hot-toast'
import { apiClient } from '@/utils/api'

export default function DemoSignupPage() {
  const [email, setEmail] = useState('')
  const [notes, setNotes] = useState('')
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address')
      return
    }

    setLoading(true)

    try {
      // Public endpoint - no auth required
      const response = await fetch('https://api.junoax.com/api/platform/demo-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: email.split('@')[0], // Use email prefix as name
          email,
          company: '',
          phone: '',
          message: notes,
          product: 'housepoints'
        }),
      })

      const data = await response.json()

      if (response.ok && data.id) {
        setSubmitted(true)
        toast.success('Request submitted! We will review it soon.')
      } else {
        toast.error(data.error || 'Failed to register demo request. Please try again.')
      }
    } catch (error: any) {
      console.error('Signup error:', error)
      toast.error('Failed to submit request. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <div className="w-full max-w-md">
          <div className="card text-center">
            <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold mb-4">Request Submitted!</h1>
            <p className="text-lg text-gray-600 mb-6">
              We've received your demo access request for <strong>{email}</strong>
            </p>
            <p className="text-gray-600 mb-8">
              Our team will review your request and send you an email with access instructions within 24 hours.
            </p>
            <button
              onClick={() => navigate('/')}
              className="w-full btn btn-primary"
            >
              Return Home
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <div className="w-full max-w-4xl">
        {/* Header */}
        <div className="card text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
              <Shield className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold gradient-text mb-4">
            Try HousePoints Demo
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Experience our family chore management system with a hands-on demo.
            Request access to explore all features with sample data.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Signup Form */}
          <div className="card">
            <h2 className="text-2xl font-semibold mb-6">Request Demo Access</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    id="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  We'll send login instructions to this email
                </p>
              </div>

              <div>
                <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                  Tell us about your family (optional)
                </label>
                <textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={4}
                  placeholder="Number of children, ages, what you're looking for..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn btn-primary flex items-center justify-center space-x-2"
              >
                {loading ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <span>Request Demo Access</span>
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>

              <p className="text-xs text-gray-500 text-center">
                By requesting access, you agree to our demo terms of use.
                Your data will be reset daily.
              </p>
            </form>
          </div>

          {/* Features */}
          <div className="space-y-6">
            <div className="card bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-blue-900 mb-2">
                    Full Family Access
                  </h3>
                  <p className="text-blue-800">
                    Try the system as both parent and child. See how chores are assigned, completed, and verified.
                  </p>
                </div>
              </div>
            </div>

            <div className="card bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Trophy className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-purple-900 mb-2">
                    Points & Rewards
                  </h3>
                  <p className="text-purple-800">
                    Experience the gamification system. Award points, track progress, and redeem rewards.
                  </p>
                </div>
              </div>
            </div>

            <div className="card bg-gradient-to-br from-pink-50 to-pink-100 border-pink-200">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-pink-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Smartphone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-pink-900 mb-2">
                    Mobile & Kiosk
                  </h3>
                  <p className="text-pink-800">
                    Test on your phone or tablet. See the kitchen kiosk dashboard in action.
                  </p>
                </div>
              </div>
            </div>

            <div className="card bg-gray-50 border-gray-200">
              <div className="flex items-center space-x-3 mb-3">
                <AlertCircle className="w-5 h-5 text-gray-600" />
                <h4 className="font-semibold text-gray-900">Demo Notes</h4>
              </div>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Demo data is shared and reset daily at midnight</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Access is typically granted within 24 hours</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Use Google sign-in or the demo password provided</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>All features available except external integrations</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Already have access */}
        <div className="mt-8 text-center">
          <p className="text-gray-600">
            Already have demo access?{' '}
            <a
              href="https://demo.housepoints.ai"
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Sign in here
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}
